import * as React from 'react';
import cx from 'classnames';
import { Helmet } from './meta';
import { CSSTransition } from 'react-transition-group';
import { addClassName } from '@elevio/styles/lib/utils/classes';
import { useSelector } from '../actions/types';
import { ActionsUnion, createAction } from '../utils/types';

// TODO: Move logic into a hook.

const UPDATE_TIME = 40;
const PROGRESS_INCREASE = 5;
// const DELAY = 100;

const LOADER_STYLES = `
  .elevio__loading-bar {
    transition: opacity 300ms, transform 200ms linear 0s;
    width: 100%;
    will-change: transform, opacity;
    height: 2px;
    background-color: blue;
    box-shadow: rgb(0, 102, 255) 0px 1px 5px 2px;
    position: fixed;
    top: 0;
  }

  .loading-enter {
    opacity: 0;
  }
  .loading-enter-active {
    opacity: 1;
  }
  .loading-exit {
    opacity: 1;
  }
  .loading-exit-active {
    opacity: 0.5;
  }
  .loading-exit-done {
    opacity: 0;
  }
`;

type LoadingBarStatus = 'hidden' | 'running';

function buildStyle(
  percent: number,
  styleProp?: React.CSSProperties,
  direction: 'rtl' | 'ltr' = 'ltr'
): React.CSSProperties {
  const coefficient = direction === 'rtl' ? 1 : -1;
  const tx = (100 - percent) * coefficient;
  const style: React.CSSProperties = {
    zIndex: 999999,
    transform: `translate3d(${tx}%, 0px, 0px)`,
  };

  return { ...style, ...styleProp };
}

const Actions = {
  start: () => createAction('start'),
  tick: () => createAction('tick'),
  finish: () => createAction('finish'),
  reset: () => createAction('reset'),
};

type Actions = ActionsUnion<typeof Actions>;

type ReducerState = {
  percent: number;
  status: LoadingBarStatus;
};

const initialState: ReducerState = {
  percent: 10,
  status: 'hidden',
};

function reducer(state: ReducerState, action: Actions) {
  switch (action.type) {
    case 'start': {
      return { status: 'running', percent: 10 } as const;
    }

    case 'tick': {
      if (state.status !== 'running') {
        return state;
      }
      // Use cosine as a smoothing function
      // It could be any function to slow down progress near the ending 100%
      const smoothedProgressIncrease = Math.floor(
        PROGRESS_INCREASE * Math.cos(state.percent * (Math.PI / 2 / 100))
      );

      const newPercent =
        state.percent === 100
          ? state.percent
          : state.percent + smoothedProgressIncrease;

      if (state.percent === newPercent) return state;

      return {
        ...state,
        percent: newPercent,
      } as const;
    }

    case 'finish':
      return { status: 'running', percent: 100 } as const;

    case 'reset':
      return { status: 'hidden', percent: 0 } as const;
  }
  return state;
}

// TODO: delay start...
// TODO: animate to end
// TODO: faster animations or only animate forward?
type LoadingBarProps = {
  className?: string;
  style?: React.CSSProperties;
};
export const LoadingBar: React.FC<LoadingBarProps> = ({
  className,
  style,
  ...props
}) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const loadingPage = useSelector((_state) => _state.page.loadingPage);
  const nodeRef = React.useRef(null);

  React.useEffect(() => {
    let progressIntervalId: number | null = null;
    let finishTimeoutId: number | null = null;

    const simulateProgress = () => {
      dispatch(Actions.tick());
    };

    if (!loadingPage) {
      progressIntervalId && clearInterval(progressIntervalId);
      dispatch(Actions.finish());
      new Promise((r) => setTimeout(r, 200)).then(() =>
        dispatch(Actions.reset())
      );
    } else if (loadingPage) {
      dispatch(Actions.start());
      progressIntervalId = window.setInterval(simulateProgress, UPDATE_TIME);
    }

    return () => {
      progressIntervalId && clearInterval(progressIntervalId);
      finishTimeoutId && clearTimeout(finishTimeoutId);
    };
  }, [loadingPage]);

  return (
    <>
      <Helmet>
        <style>{LOADER_STYLES}</style>
      </Helmet>
      <CSSTransition
        nodeRef={nodeRef}
        in={state.status !== 'hidden'}
        timeout={300}
        classNames="loading"
        unmountOnExit
      >
        <div
          ref={nodeRef}
          style={buildStyle(state.percent, style)}
          className={cx(className, state.status, addClassName('loading-bar'))}
          {...props}
        />
      </CSSTransition>
    </>
  );
};
